"use client";

import * as React from "react";
import type { Cover } from "../../data/work";
import { usePrefersReducedMotion } from "../../hooks";

/**
 * A cover still that upgrades itself to a looping clip.
 *
 * The video is only attached once the element is near the viewport, so nobody
 * pays for it while reading the top of the page, and it pauses again on the
 * way out. Under a reduced-motion preference it stays a still image and offers
 * a manual play control instead of moving on its own.
 */
export default function CoverMedia({ cover }: { cover: Cover }) {
  const wrapRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const reduced = usePrefersReducedMotion();

  const [attach, setAttach] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);

  const hasVideo = Boolean(cover.video);

  React.useEffect(() => {
    const el = wrapRef.current;
    if (!el || !hasVideo || typeof IntersectionObserver === "undefined") return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setAttach(true);
        const v = videoRef.current;
        if (!v) return;
        if (entry.isIntersecting) {
          if (!reduced) v.play().catch(() => {});
        } else {
          v.pause();
        }
      },
      { rootMargin: "200px 0px", threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasVideo, reduced]);

  return (
    <div ref={wrapRef} className="relative">
      {hasVideo && attach ? (
        <>
          <video
            ref={videoRef}
            className="aspect-[16/9] w-full bg-raised object-cover"
            poster={cover.src}
            src={cover.video}
            muted
            loop
            playsInline
            preload="metadata"
            autoPlay={!reduced}
            aria-label={cover.videoAlt ?? cover.alt}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
          <button
            type="button"
            onClick={() => {
              const v = videoRef.current;
              if (!v) return;
              if (v.paused) v.play().catch(() => {});
              else v.pause();
            }}
            className="absolute bottom-2.5 right-2.5 rounded-md border border-white/20 bg-black/55 px-2 py-1 font-mono text-2xs text-white/90 backdrop-blur transition-colors hover:bg-black/75"
          >
            {playing ? "Pause" : "Play"}
          </button>
        </>
      ) : (
        <img
          src={cover.src}
          alt={cover.alt}
          loading="lazy"
          decoding="async"
          className="aspect-[16/9] w-full object-cover"
        />
      )}
    </div>
  );
}
